import type { ReactNode } from "react";

// Box-internal section header. Sits as the first child of a padded `.box`,
// title left, optional right slot (count, toggle, link). Replaces the old
// "title above the box" convention; see docs/UI_CONVENTIONS.md.
//
// Variants:
//   default - `text-smycles-title font-medium` (18px). The everyday section header.
//   hero    - `text-smycles-display-xl leading-none tracking-tight` (~48px). Used on
//             landing-style boxes (e.g. Save · Service "Input"/"Output"/
//             "Documents") where the box is the visual centrepiece. Same
//             font family as `default`, only the size changes.
//
// `wrap` lets the right slot drop under the title when both do not fit on one
// line, e.g. a three-option SegmentedControl in a sidebar box. On its own row
// it sits flush left with the title, not right-aligned: a control hanging off
// the right edge under a left-aligned title reads as misplaced. Off by
// default: most right slots are a count or one toggle, and a long title should
// keep wrapping its own text rather than push a small badge onto a second row.

interface BoxHeaderProps {
  title?: ReactNode;
  right?: ReactNode;
  className?: string;
  variant?: "default" | "hero";
  wrap?: boolean;
}

export function BoxHeader({
  title,
  right,
  className = "",
  variant = "default",
  wrap = false,
}: BoxHeaderProps) {
  const titleCls =
    variant === "hero"
      ? "text-smycles-display-xl font-medium leading-none tracking-tight text-ink"
      : "text-smycles-title font-medium text-ink";
  const wrapperCls =
    variant === "hero"
      ? "mb-3 flex items-start justify-between gap-3"
      : "mb-4 flex min-h-7 items-center justify-between gap-3";
  // justify-between only spreads items that share a line; a lone second-row item
  // parks at the start, which is exactly the flush-left placement wrap wants. The
  // slot also gives up shrink-0 there: once alone on a row, being narrower than its
  // content is what lets a flex-wrap group inside it fold (toggle above control).
  const rightCls = wrap ? "" : "shrink-0";
  return (
    <div className={`${wrapperCls}${wrap ? " flex-wrap" : ""} ${className}`}>
      {title != null ? <div className={titleCls}>{title}</div> : null}
      {right != null ? <div className={rightCls}>{right}</div> : null}
    </div>
  );
}
